/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  IconAdjustmentsHorizontal,
  IconArrowDown,
  IconLoader2,
  IconDownload,
  IconArrowUp,
  IconArrowsUpDown,
  IconTrash,
  IconCheck,
} from '@tabler/icons-react';
import {
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@common/utils';
import { QueryTag, SearchParam, TableType } from '@common/ts';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import useTranslation from '@/hooks/useTranslation';
import { getSortedTranslatedLanguages } from '@/utils/getSortedTranslatedLanguages';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import TranscriptsColumns from './TranscriptsColumns';
import 'react-toastify/dist/ReactToastify.css';
import { Checkbox } from '@/components/ui/checkbox';
import {
  useMutateGenerateTranscriptsTranslation,
  useMutateDeleteConversations,
  useMutateMarkConversationsComplete,
} from '@/reactQuery/post';
import getUniqueMetadataKeys from '@/utils/getUniqueMetadataKeys';
import { Input } from '@/components/ui/input';
import replaceUnderscoreWithSpace from '@/utils/transformUnderscoreToSpace';
import calculateConversationDuration from '@/utils/calculateConversationDuration';
import formatDate from '@/utils/dateFormat';
import DataTablePagination from '@/components/paginations/DataTablePagination';
import { TableData } from '@/types';
import languagesList from '@/lang/languages/languagesList';
import ConfirmTranslationModal from '@/components/Modals/ConfirmTranslationModal';
import ExportModal from '@/components/Modals/ExportModal/ExportModal';
import ConfirmDeleteConversationsModal from '@/components/Modals/ConfirmDeleteConversationsModal';
import ConfirmMarkCompleteModal from '@/components/Modals/ConfirmMarkCompleteModal';
import TranscriptsTranslationDropdown from './TranscriptsTranslationDropdown';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';
import FiltersSelect from '@/components/projects/single-project/filters/FiltersSelect';
import TranscriptsViewToggle from '@/components/transcriptsViewToggle/TranscriptsViewToggle';
import { TranscriptsView, EProjectStatus } from '@/enums';
import { getPageCount } from '@/utils/getPageCount';
import { getPaginationParamPage } from '@/utils/urlParams';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';

export default function TranscriptsTable({
  allConversations,
  projectId,
  languages,
  initialTranslatedLanguages,
  completedConversationsLength,
  showFilters = false,
  onReRun,
  rerunFilterValues = [],
  onFilterDeleteEnableRefetch,
  refetch,
  activeView = TranscriptsView.COMPLETED,
  onViewChange,
  completedCount = 0,
  incompleteCount = 0,
  screenedOutCount = 0,
  totalCount = 0,
  isLoadingConversations = false,
  showIncompleteToggle = true,
  showScreenedOutToggle = true,
  projectStatus,
  deletedConversations,
  isCompletedTranscriptsView,
  scrollToConversation,
  onScrollTo,
  // URL table props received from parent
  urlCurrentPage,
  updateUrlPage,
  urlSorting,
  updateUrlSorting,
  updateUrlFilter,
  getUrlFilterValue,
}: TableData) {
  const languageListwithOriginal = languagesList.language.map(({ label }) => label);
  const {
    setTranslationsLoading,
    translationsLoading,
    setTranslating,
    isTranslating,
    setTranslatingProjects,
    translatingProjects,
  } = useTranslationsLoadingStore();
  const { lang } = useTranslation();
  const { isUserEditor, isUserOwner } = useWorkspaceRoles();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMarkCompleteModalOpen, setIsMarkCompleteModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const hasTranslationPagination = !!searchParams.get(
    getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS),
  );
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const activeLanguage = translationLanguage || initialTranslatedLanguages[0];
  const [dropdownLanguage, setDropdownLanguage] = useState<string | null>(null);
  const [translatedLanguages, setTranslatedLanguages] = useState<string[]>([]);
  const [unTranslatedLanguages, setUntranslatedLanguages] = useState<string[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [pendingMarkCompleteIds, setPendingMarkCompleteIds] = useState<string[]>([]);
  const isProjectPublished = projectStatus === EProjectStatus.PUBLISHED;
  const canDeleteConversation =
    (isUserEditor || isUserOwner) && isProjectPublished && showIncompleteToggle;
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const [inputValue, setInputValue] = useState<Record<string, string>>({});

  const queryClient = useQueryClient();
  const { mutateAsync } = useMutateGenerateTranscriptsTranslation(projectId);
  const { mutateAsync: deleteConversations, isPending: isDeletingConversations } =
    useMutateDeleteConversations();
  const { mutateAsync: markConversationsComplete, isPending: isMarkingComplete } =
    useMutateMarkConversationsComplete();

  const uniqueMetadataKeys = useMemo(() => {
    if (allConversations) {
      return getUniqueMetadataKeys(allConversations);
    }
    return new Set<string>();
  }, [allConversations]);

  const currentProjectIsTranslating = translatingProjects?.find(
    (project) => project.projectId === projectId,
  );
  const metadataKeysArray = Array.from(uniqueMetadataKeys);

  const columns = TranscriptsColumns(metadataKeysArray, activeView);

  const conversationsWithDuration = useMemo(
    () =>
      allConversations.map((conversation) => ({
        ...conversation,
        // Use startedAt if available, otherwise fall back to createdAt (for screened out conversations that never started)
        createdAt: formatDate(
          conversation.startedAt || conversation.createdAt,
          'DD/MMM/YYYY - hh:mm:ss A',
        ),
        duration: calculateConversationDuration({
          startedAt: conversation.startedAt,
          finishedAt: conversation.finishedAt,
        }),
      })),
    [allConversations],
  );

  const handleLanguageSelection = async (language: string) => {
    searchParams.delete(SearchParam.SELECTED_TRANSCRIPT);
    if (hasTranslationPagination) {
      searchParams.delete(getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS));
    }
    if (translatedLanguages?.includes(language) || language === initialTranslatedLanguages[0]) {
      if (language === initialTranslatedLanguages[0]) {
        searchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
      } else {
        searchParams.set(SearchParam.TRANSLATION_LANGUAGE, language);
      }
      setSearchParams(searchParams);
    } else {
      setDropdownLanguage(language);
      setIsModalOpen(true);
    }
  };

  const confirmTranslation = async () => {
    if (!dropdownLanguage || !completedConversationsLength) return;
    setIsModalOpen(false);
    setTranslating(true);
    toast.info(lang.get('msg.translationMightTakeAWhile'));
    setTranslationsLoading(true);
    setSearchParams({ [SearchParam.TRANSLATION_LANGUAGE]: dropdownLanguage });
    setTranslatingProjects([
      ...translatingProjects,
      { projectId, language: dropdownLanguage, length: completedConversationsLength },
    ]);
    try {
      await mutateAsync({ languages: [dropdownLanguage] });
    } catch (error) {
      toast.error(lang.get('msg.errorTranslatingConversations'));
      setTranslating(false);
      setTranslationsLoading(false);
    }
  };

  // Calculate page count using totalCount
  const PAGE_SIZE = 20;
  const pageCount = getPageCount(totalCount, PAGE_SIZE);

  const table = useReactTable({
    data: conversationsWithDuration || [],
    columns,
    pageCount,
    onSortingChange: updateUrlSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    state: {
      sorting: urlSorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: urlCurrentPage - 1,
        pageSize: PAGE_SIZE,
      },
    },
  });

  useEffect(() => {
    // Clear and apply only filters for current table type
    table.getAllColumns().forEach((column) => {
      column.setFilterValue('');

      const columnId = column.id;
      const filterValue = getUrlFilterValue(columnId);
      if (filterValue) {
        column.setFilterValue(filterValue);
      }
    });
  }, [table, getUrlFilterValue]);

  const handleInputChange = useCallback(
    (columnId: string, value: string) => {
      setInputValue((prev) => ({
        ...prev,
        [columnId]: value,
      }));
      updateUrlFilter(columnId, value);
    },
    [updateUrlFilter],
  );

  useEffect(() => {
    // Clear and apply only filters for current table type
    table.getAllColumns().forEach((column) => {
      column.setFilterValue('');

      const columnId = column.id;
      const filterValue = getUrlFilterValue(columnId);
      if (filterValue) {
        column.setFilterValue(filterValue);
      }
    });
  }, [table, getUrlFilterValue]);

  useEffect(() => {
    setTranslatedLanguages(initialTranslatedLanguages);
    setUntranslatedLanguages(
      languageListwithOriginal.filter(
        (language) => !initialTranslatedLanguages?.includes(language),
      ),
    );
  }, [initialTranslatedLanguages]);

  useEffect(() => {
    if (!currentProjectIsTranslating || currentProjectIsTranslating.length === 0) return;
    if (currentProjectIsTranslating && !!completedConversationsLength) {
      setTranslationsLoading(false);
    }

    if (currentProjectIsTranslating?.length === completedConversationsLength) {
      queryClient.invalidateQueries({
        queryKey: [QueryTag.CONVERSATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_LANGUAGES(projectId),
      });
      toast.success(lang.get('msg.translationCompleted'));
      setTranslating(false);
      const translatedProjects = translatingProjects.filter(
        (project) => project.projectId !== projectId,
      );
      setTranslatingProjects([...translatedProjects]);
    }
  }, [completedConversationsLength, currentProjectIsTranslating]);

  useEffect(() => {
    if (languages?.length && !isTranslating) {
      setTranslatedLanguages((prev) => {
        const newLanguages = languages.filter((language) => !prev?.includes(language));
        return [...prev, ...newLanguages];
      });

      setUntranslatedLanguages(
        languagesList.language
          .map(({ label }) => label)
          .filter((language) => !languages?.includes(language) && language !== activeLanguage),
      );
    }
  }, [languages, activeLanguage, isTranslating, projectId]);

  const selectedConversationIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const selectedCount = selectedConversationIds.length;

  const handleDeleteConversations = async () => {
    if (selectedCount === 0) return;

    // Close modal immediately and clear selection
    setIsDeleteModalOpen(false);
    setRowSelection({});

    // Track which conversations are pending deletion
    setPendingDeleteIds(selectedConversationIds);

    try {
      await deleteConversations({
        project_id: projectId,
        conversationIds: selectedConversationIds,
      });

      const message = `${
        selectedCount === 1 ? 'Conversation' : 'Conversations'
      } deleted successfully`;

      toast.success(message);
    } catch (error: any) {
      const fallbackMessage = `An error occurred and ${
        selectedCount === 1 ? 'conversation' : 'conversations'
      } could not be deleted`;

      const errorMessage = getErrorMessage(error, fallbackMessage);
      toast.error(errorMessage);

      // Clear pending deletes on error
      setPendingDeleteIds([]);
    } finally {
      // Invalidate conversations cache to trigger a fresh fetch
      await queryClient.invalidateQueries({
        queryKey: [QueryTag.CONVERSATIONS],
      });
      // Also trigger the original refetch for the run state
      if (refetch) {
        refetch();
      }
    }
  };

  const handleDeleteButtonClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleMarkConversationsComplete = async () => {
    if (selectedCount === 0) return;

    // Close modal immediately and clear selection
    setIsMarkCompleteModalOpen(false);
    setRowSelection({});

    // Track which conversations are pending
    setPendingMarkCompleteIds(selectedConversationIds);

    try {
      const result = await markConversationsComplete({
        projectId,
        conversationIds: selectedConversationIds,
      });

      const markedCount = result?.data?.markedCount || selectedCount;
      toast.success(`${markedCount} ${lang.get('msg.conversationsMarkedComplete')}`);
    } catch (error: any) {
      const fallbackMessage = `An error occurred and ${
        selectedCount === 1 ? 'conversation' : 'conversations'
      } could not be marked as complete`;

      const errorMessage = getErrorMessage(error, fallbackMessage);
      toast.error(errorMessage);

      // Clear pending on error
      setPendingMarkCompleteIds([]);
    }
  };

  const handleMarkCompleteButtonClick = () => {
    setIsMarkCompleteModalOpen(true);
  };

  const handleSelectAll = useCallback(() => {
    if (selectedCount === conversationsWithDuration?.length) {
      // Deselect all
      setRowSelection({});
    } else {
      // Select all
      const allSelection: Record<string, boolean> = {};
      conversationsWithDuration?.forEach((conversation) => {
        const rowId = conversation.conversation_id || conversation._id;
        allSelection[rowId] = true;
      });
      setRowSelection(allSelection);
    }
  }, [selectedCount, conversationsWithDuration]);

  const isAllSelected =
    conversationsWithDuration?.length > 0 && selectedCount === conversationsWithDuration?.length;

  const isIndeterminate =
    selectedCount > 0 && selectedCount < (conversationsWithDuration?.length || 0);
  // Update indeterminate state for select all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Clear selection when switching between completed/incomplete tabs
  useEffect(() => {
    setRowSelection({});
  }, [activeView]);

  useEffect(() => {
    if (scrollToConversation && rowRefs.current[scrollToConversation]) {
      rowRefs.current[scrollToConversation]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      onScrollTo(null);
    }
  }, [scrollToConversation]);

  // Clear pending deletes when conversations are actually removed from the data
  useEffect(() => {
    if (pendingDeleteIds.length > 0 && allConversations) {
      const currentConversationIds = allConversations.map(
        (conv) => conv.conversation_id || conv._id,
      );

      const stillPendingIds = pendingDeleteIds.filter((id) => currentConversationIds.includes(id));

      if (stillPendingIds.length < pendingDeleteIds.length) {
        setPendingDeleteIds(stillPendingIds);
      }
    }
  }, [allConversations, pendingDeleteIds]);

  // Clear pending mark complete ids when conversations are removed from the data
  useEffect(() => {
    if (pendingMarkCompleteIds.length > 0 && allConversations) {
      const currentConversationIds = allConversations.map(
        (conv) => conv.conversation_id || conv._id,
      );

      const stillPendingIds = pendingMarkCompleteIds.filter((id) =>
        currentConversationIds.includes(id),
      );

      if (stillPendingIds.length < pendingMarkCompleteIds.length) {
        setPendingMarkCompleteIds(stillPendingIds);
      }
    }
  }, [allConversations, pendingMarkCompleteIds]);

  return (
    <>
      <div className="w-full">
        <div className="flex flex-wrap items-center w-full pt-4 pb-2">
          <div className="flex items-center justify-between w-full gap-4 mb-2">
            {/* Left side - Filters, Completed/Incomplete Toggle, and Delete Button */}
            <div className="flex items-end gap-2">
              {/* Filters */}
              {showFilters && onReRun && onFilterDeleteEnableRefetch && refetch && (
                <FiltersSelect
                  onReRun={onReRun}
                  rerunFilterValues={rerunFilterValues}
                  onFilterDeleteEnableRefetch={onFilterDeleteEnableRefetch}
                  refetch={refetch}
                />
              )}

              {/* Completed/Incomplete Toggle */}
              {showIncompleteToggle && onViewChange && (
                <TranscriptsViewToggle
                  activeView={activeView}
                  onViewChange={onViewChange}
                  completedCount={completedCount}
                  incompleteCount={incompleteCount}
                  screenedOutCount={screenedOutCount}
                  isLoading={isLoadingConversations}
                  showIncompleteToggle={showIncompleteToggle}
                  showScreenedOutToggle={showScreenedOutToggle}
                />
              )}

              {/* Delete Button + Counter above it */}
              {canDeleteConversation && (
                <div className="flex flex-col items-end justify-end min-w-[120px]">
                  {/* Count label above the button */}
                  {!!deletedConversations && (
                    <div className="w-full flex items-center text-sm mb-1 text-gray-400 font-semibold">
                      <IconTrash size={16} stroke={2.5} className="mr-1 text-gray-400" />
                      <span>
                        {deletedConversations} {lang.get('msg.deleted').toLowerCase()}
                      </span>
                    </div>
                  )}

                  <Button
                    variant="destructive"
                    onClick={handleDeleteButtonClick}
                    disabled={isDeletingConversations || selectedCount === 0}
                    className="flex items-center gap-2 w-full"
                  >
                    {isDeletingConversations && <IconLoader2 className="animate-spin" size={16} />}
                    {lang.get('msg.delete')} ({selectedCount})
                  </Button>
                </div>
              )}

              {/* Mark as Complete Button - Only on Incomplete view */}
              {canDeleteConversation && activeView === TranscriptsView.INCOMPLETE && (
                <Button
                  variant="default"
                  onClick={handleMarkCompleteButtonClick}
                  disabled={isMarkingComplete || selectedCount === 0}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  {isMarkingComplete ? (
                    <IconLoader2 className="animate-spin" size={16} />
                  ) : (
                    <IconCheck size={16} />
                  )}
                  {lang.get('msg.markAsComplete')} ({selectedCount})
                </Button>
              )}
            </div>

            {/* Right side - Language, Export, and View controls */}
            <div className="flex items-center gap-2">
              {!!completedConversationsLength && isCompletedTranscriptsView && (
                <TranscriptsTranslationDropdown
                  isLoading={!!currentProjectIsTranslating}
                  activeLanguage={activeLanguage! || dropdownLanguage}
                  translatedLanguages={getSortedTranslatedLanguages(
                    initialTranslatedLanguages,
                    translatedLanguages,
                  )}
                  unTranslatedLanguages={unTranslatedLanguages}
                  handleLanguageSelection={handleLanguageSelection}
                />
              )}

              {!translationLanguage && (
                <Button
                  variant="default"
                  onClick={() => setIsExportModalOpen(true)}
                  className="flex items-center gap-1"
                >
                  <IconDownload size={18} />
                  {lang.get('msg.export')}
                </Button>
              )}
              {/* column filters */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="ml-auto text-dark-text">
                    <IconAdjustmentsHorizontal size={16} className="mr-2" />
                    {lang.get('msg.view')}
                    <ChevronDown className="w-4 h-4 ml-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column?.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {column?.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        {!translationsLoading &&
        !isDeletingConversations &&
        !isMarkingComplete &&
        pendingDeleteIds.length === 0 &&
        pendingMarkCompleteIds.length === 0 ? (
          <div className="border rounded-md text-dark-text">
            <Table>
              <TableHeader>
                {table?.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup?.id}>
                    {canDeleteConversation && table?.getRowModel().rows?.length > 0 && (
                      <TableHead>
                        <Checkbox
                          ref={selectAllCheckboxRef}
                          className="cursor-pointer"
                          checked={isAllSelected}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                    )}
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header?.id}>
                        <div className='flex items-center w-full gap-2"'>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {header?.id !== 'actions' && header?.id !== 'participants' && (
                            <div>
                              {{
                                asc: <IconArrowUp size={18} />,
                                desc: <IconArrowDown size={18} />,
                              }[header.column.getIsSorted() as string] ?? (
                                <IconArrowsUpDown size={18} />
                              )}
                            </div>
                          )}
                        </div>
                        {header?.id !== 'actions' && header?.id !== 'participants' && (
                          <Input
                            placeholder={`${lang.get('msg.search')} ${replaceUnderscoreWithSpace(
                              header?.id,
                            )}...`}
                            value={
                              getUrlFilterValue(header.id) ||
                              (inputValue[header.id] as string) ||
                              ''
                            }
                            onChange={(e) => handleInputChange(header.id, e.target.value)}
                            className="w-full mb-2.5 min-w-52 max-w-52 bg-white"
                          />
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table?.getRowModel().rows?.length ||
                (currentProjectIsTranslating?.length ?? 0) > 0 ? (
                  table?.getRowModel().rows.map((row) => {
                    const rowId = row.original?.conversation_id || row.original?._id;
                    return (
                      <TableRow
                        key={rowId}
                        data-state={row.getIsSelected() && 'selected'}
                        ref={(el) => {
                          rowRefs.current[rowId] = el;
                        }}
                      >
                        {canDeleteConversation && (
                          <TableCell className="py-0 h-[60px]">
                            <Checkbox
                              className="cursor-pointer"
                              checked={!!rowSelection[rowId]}
                              onCheckedChange={() => {
                                setRowSelection((prev) => ({
                                  ...prev,
                                  [rowId]: !prev[rowId],
                                }));
                              }}
                            />
                          </TableCell>
                        )}
                        {row.getVisibleCells().map((cell) => (
                          <TableCell className="py-0 h-[60px]" key={cell?.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns?.length} className="h-24 text-center">
                      {lang.get('msg.noResults')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex items-center justify-center w-full h-80">
            <IconLoader2 className="animate-spin" size={30} />
          </div>
        )}
        <DataTablePagination
          table={table}
          currentPage={urlCurrentPage}
          onPageChange={updateUrlPage}
        />
      </div>
      <ConfirmTranslationModal
        isOpen={isModalOpen}
        onConfirm={confirmTranslation}
        onCancel={() => {
          setDropdownLanguage(activeLanguage);
          setIsModalOpen(false);
        }}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        projectId={projectId!}
        transcriptsView={activeView}
      />
      <ConfirmDeleteConversationsModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConversations}
        conversationCount={selectedCount}
        activeView={activeView}
        isLoading={isDeletingConversations}
      />
      <ConfirmMarkCompleteModal
        isOpen={isMarkCompleteModalOpen}
        onClose={() => setIsMarkCompleteModalOpen(false)}
        onConfirm={handleMarkConversationsComplete}
        conversationCount={selectedCount}
        hasInProgressConversations={allConversations
          .filter((conv) => selectedConversationIds.includes(conv.conversation_id || conv._id))
          .some((conv) => conv.status === 'in_progress')}
        isLoading={isMarkingComplete}
      />
    </>
  );
}
